import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FileText, Plus, ScrollText } from 'lucide-react';
import toast from 'react-hot-toast';
import FormSection from '../../components/Common/FormSection';
import { processService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';
import { useTenant } from '../../contexts/TenantContext';
import {
  DESPACHO_PLACEHOLDERS,
  DESPACHO_TIPOS,
  defaultDespachoCorpo,
  despachoStatusMeta,
  despachoTipoLabel,
} from '../../utils/despachoStatus';

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? [];
}

function openPdfBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'despacho.pdf';
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export default function ProcessoDespachoSection({ processoId, processoNumero, requester }) {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    tipo: 'outro',
    titulo: 'Despacho',
    corpo: defaultDespachoCorpo({ processoNumero, orgaoNome: tenant?.name }),
  });

  const { data, isLoading } = useQuery(
    ['despachos-processo', processoId],
    () => processService.listDespachos(processoId),
    { enabled: !!processoId }
  );
  const rows = unwrap(data);

  const invalidate = () => queryClient.invalidateQueries(['despachos-processo', processoId]);

  const saveMut = useMutation(
    async () => {
      const payload = {
        processo_id: processoId,
        tipo: form.tipo,
        titulo: form.titulo.trim() || 'Despacho',
        corpo: form.corpo,
      };
      if (editingId) return processService.updateDespacho(editingId, payload);
      return processService.createDespacho(payload);
    },
    {
      onSuccess: (row) => {
        toast.success(editingId ? 'Rascunho atualizado' : 'Rascunho salvo');
        setEditingId(row.id);
        invalidate();
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const publishMut = useMutation(
    async () => {
      let id = editingId;
      if (!id) {
        const created = await processService.createDespacho({
          processo_id: processoId,
          tipo: form.tipo,
          titulo: form.titulo.trim() || 'Despacho',
          corpo: form.corpo,
        });
        id = created.id;
        setEditingId(id);
      } else {
        await processService.updateDespacho(id, {
          tipo: form.tipo,
          titulo: form.titulo.trim() || 'Despacho',
          corpo: form.corpo,
        });
      }
      return processService.publishDespacho(id);
    },
    {
      onSuccess: () => {
        toast.success('Despacho publicado');
        setEditingId(null);
        setForm({
          tipo: 'outro',
          titulo: 'Despacho',
          corpo: defaultDespachoCorpo({ processoNumero, orgaoNome: tenant?.name }),
        });
        invalidate();
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const deleteMut = useMutation((id) => processService.deleteDespacho(id), {
    onSuccess: (_, id) => {
      toast.success('Rascunho excluído');
      if (editingId === id) {
        setEditingId(null);
        setForm({
          tipo: 'outro',
          titulo: 'Despacho',
          corpo: defaultDespachoCorpo({ processoNumero, orgaoNome: tenant?.name }),
        });
      }
      invalidate();
    },
    onError: (e) => toast.error(userFacingError(e, 'deleteError')),
  });

  const preview = async (row) => {
    try {
      if (row?.status === 'publicado') {
        const { data: blob } = await processService.downloadDespachoPdf(row.id);
        openPdfBlob(blob, `${row.numero}.pdf`);
        return;
      }
      const { data: blob } = await processService.previewDespachoPdf({
        processo_id: processoId,
        tipo: row?.tipo || form.tipo,
        titulo: row?.titulo || form.titulo,
        corpo: (row?.corpo || form.corpo || '').trim(),
        numero: row?.numero,
      });
      openPdfBlob(blob, `${row?.numero || 'despacho'}.pdf`);
    } catch (e) {
      toast.error(userFacingError(e, 'loadError'));
    }
  };

  const startNew = () => {
    setEditingId(null);
    setForm({
      tipo: 'outro',
      titulo: 'Despacho',
      corpo: defaultDespachoCorpo({ processoNumero, orgaoNome: tenant?.name }),
    });
  };

  const loadRow = (row) => {
    setEditingId(row.id);
    setForm({
      tipo: row.tipo || 'outro',
      titulo: row.titulo || 'Despacho',
      corpo: row.corpo || '',
    });
  };

  const insertPlaceholder = (key) => {
    const token = `{{${key}}}`;
    setForm((prev) => ({ ...prev, corpo: `${prev.corpo || ''}${token}` }));
  };

  const canPublish = useMemo(() => (form.corpo || '').trim().length >= 3, [form.corpo]);

  return (
    <FormSection title="Despachos" icon={ScrollText} variant="primary">
      <p className="mb-4 text-sm text-slate-600">
        Documento oficial do órgão, com cabeçalho, rodapé e marca d’água da identidade cadastrada.
        O PDF do processo inclui a capa e todos os despachos publicados em anexo.
      </p>

      {isLoading ? (
        <p className="text-sm text-slate-500">Carregando despachos…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">
          {requester ? 'Nenhum despacho publicado neste processo.' : 'Ainda não há despacho neste processo.'}
        </p>
      ) : (
        <ul className="mb-5 divide-y divide-slate-100 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {rows.map((row) => {
            const meta = despachoStatusMeta(row.status);
            return (
              <li key={row.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-900">{row.numero}</span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        meta.tone === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {meta.label}
                    </span>
                    <span className="text-xs text-slate-500">{despachoTipoLabel(row.tipo)}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-600">{row.titulo}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="btn btn-outline" onClick={() => preview(row)}>
                    {row.status === 'publicado' ? 'Baixar PDF' : 'Pré-visualizar'}
                  </button>
                  {!requester && row.status === 'rascunho' ? (
                    <>
                      <button type="button" className="btn btn-outline" onClick={() => loadRow(row)}>
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline text-red-700"
                        onClick={() => deleteMut.mutate(row.id)}
                      >
                        Excluir
                      </button>
                    </>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {requester ? null : (
        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-slate-900">
              {editingId ? 'Editar rascunho' : 'Novo despacho'}
            </h3>
            {editingId ? (
              <button type="button" className="text-sm font-medium text-primary-700" onClick={startNew}>
                <Plus className="mr-1 inline h-4 w-4" />
                Limpar
              </button>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="form-group">
              <span className="form-label">Tipo</span>
              <select
                className="form-input"
                value={form.tipo}
                onChange={(e) => setForm((prev) => ({ ...prev, tipo: e.target.value }))}
              >
                {DESPACHO_TIPOS.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-group">
              <span className="form-label">Título</span>
              <input
                className="form-input"
                value={form.titulo}
                onChange={(e) => setForm((prev) => ({ ...prev, titulo: e.target.value }))}
              />
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {DESPACHO_PLACEHOLDERS.map((item) => (
              <button
                key={item.key}
                type="button"
                className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600"
                onClick={() => insertPlaceholder(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <label className="form-group mt-3">
            <span className="form-label">Corpo</span>
            <textarea
              className="form-input min-h-[220px] font-mono text-sm"
              value={form.corpo}
              onChange={(e) => setForm((prev) => ({ ...prev, corpo: e.target.value }))}
            />
          </label>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline inline-flex items-center gap-2"
              disabled={saveMut.isLoading}
              onClick={() => saveMut.mutate()}
            >
              <FileText className="h-4 w-4" />
              Salvar rascunho
            </button>
            <button
              type="button"
              className="btn btn-outline"
              disabled={!canPublish}
              onClick={() => preview(editingId ? { ...form, id: editingId, numero: rows.find((r) => r.id === editingId)?.numero } : form)}
            >
              Pré-visualizar PDF
            </button>
            <button
              type="button"
              className="btn btn-primary inline-flex items-center gap-2"
              disabled={!canPublish || publishMut.isLoading}
              onClick={() => publishMut.mutate()}
            >
              <ScrollText className="h-4 w-4" />
              Publicar despacho
            </button>
          </div>
        </div>
      )}
    </FormSection>
  );
}
